import type { Nullable } from "@core/domain/types/nullable.type";
import { normalizeText } from "@core/domain/utils/normalize-text";
import { BaseViewModel } from "@iot-platform/core";
import {
    SpatialLevel,
    SpatialLevels,
} from "@station/domain/models/spatial-level.model";
import type {
    Station,
    StationListSearchFilters,
} from "@station/domain/models/station.model";
import { SpatialLevelGetAllUseCase } from "@station/domain/usecases/spatial-level-get-all.usecase";
import { StationAddSelectedUseCase } from "@station/domain/usecases/station-add-selected.usecase";
import { StationGetSelectedUseCase } from "@station/domain/usecases/station-get-selected.usecase";
import { StationRemoveSelectedUseCase } from "@station/domain/usecases/station-remove-selected.usecase";
import type { SearchResult } from "@station/presentation/components/list/station-list.component.tsx";
import { getInitialSuggestions } from "@station/presentation/utils/station-suggestions.utils";
import { inject, injectable } from "inversify";
import { action, makeObservable, observable, runInAction } from "mobx";

@injectable()
export class StationListViewmodel extends BaseViewModel {
    @observable
    spatialLevels: SpatialLevels = SpatialLevels.empty();

    @observable
    selectedStations: readonly Readonly<Station>[] = [];

    initialFiltersValue: StationListSearchFilters = {
        search: "",
    };

    @observable filters: StationListSearchFilters = this.initialFiltersValue;

    @observable
    searchResults: {
        label: string;
        id: number;
        isStation?: boolean;
        latitude?: Nullable<string>;
        longitude?: Nullable<string>;
        icon?: string;
    }[] = [];

    constructor(
        @inject(SpatialLevelGetAllUseCase)
        private readonly spatialLevelGetAllUseCase: SpatialLevelGetAllUseCase,
        @inject(StationGetSelectedUseCase)
        private readonly stationGetSelectedUseCase: StationGetSelectedUseCase,
        @inject(StationAddSelectedUseCase)
        private readonly stationAddSelectedUseCase: StationAddSelectedUseCase,
        @inject(StationRemoveSelectedUseCase)
        private readonly stationRemoveSelectedUseCase: StationRemoveSelectedUseCase,
    ) {
        super();
        makeObservable(this);
    }

    @action
    override async didMount(): Promise<void> {
        await this.initViewData();

        this.addSub(
            this.stationGetSelectedUseCase.execute().subscribe((stations) => {
                runInAction(() => {
                    this.selectedStations = stations;
                });
            }),
        );
    }

    async initViewData(): Promise<void> {
        await this.fetchSpatialLevels();
    }

    @action
    async fetchSpatialLevels(forceRemote = false): Promise<void> {
        const spatialLevels =
            await this.spatialLevelGetAllUseCase.execute(forceRemote);
        runInAction(() => {
            this.setSpatialLevels(spatialLevels);
        });
    }

    @action
    addStation(station: Readonly<Station> | Readonly<Station>[]): void {
        this.stationAddSelectedUseCase.execute(station);
    }

    @action
    removeStation(station: Readonly<Station> | Readonly<Station>[]): void {
        this.stationRemoveSelectedUseCase.execute(station);
    }

    @action
    setSpatialLevels(spatialLevels: SpatialLevels): void {
        this.spatialLevels = spatialLevels;
    }

    @action
    setFilters(filters: { search: string }): void {
        this.filters = filters;
        this.updateSearchResults();
    }

    // Updates the search results based on the current search query
    @action
    updateSearchResults(): void {
        const query = normalizeText(this.filters.search);

        if (!query) {
            this.searchResults = getInitialSuggestions(
                this.spatialLevels.spatialLevels,
            );
            return;
        }

        const searchResults = new Map<number, SearchResult>();

        // Recursive function to search through spatial levels and stations
        const processLevel = (level: Readonly<SpatialLevel>): void => {
            if (normalizeText(level.toLabel).includes(query)) {
                searchResults.set(level.id.valueOrThrow, {
                    label: level.toLabel,
                    id: level.id.valueOrThrow,
                    isStation: false,
                });
            }

            level.stations.forEach((station) => {
                if (normalizeText(station.displayName).includes(query)) {
                    searchResults.set(station.id.valueOrThrow, {
                        label: station.displayName,
                        id: station.id.valueOrThrow,
                        isStation: true,
                        icon: station.icon,
                    });
                }
            });

            level.children?.forEach(processLevel);
        };

        // Iterate through all spatial levels and process them
        this.spatialLevels.spatialLevels.forEach(processLevel);

        // Convert results map to an array and update searchResults
        this.searchResults = Array.from(searchResults.values());
    }
}
